import type { Msg, Session } from '@halo/chat-sdk-react';
import {
  MessageMetaRoot,
  toaster,
  MessageLuckyBox,
  MessageBubble,
  ItemExpireContext as MessageExpireContext,
} from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useUserInfo } from '@halo/chat-sdk-react';
import { Center } from '@chakra-ui/react';
import {
  GroupStatusEnum,
  LuckyBoxGrabStatus,
  useAppStore,
  useStaticAppStore,
} from '@/store/client';
import {
  previewMsgLuckyBoxGrayImg,
  previewMsgLuckyBoxImg,
  previewMsgLuckyBoxOpenedImg,
} from '@/assets/images/luckyBox';
import { useWalletStore } from '@/wallet';
import { LuckyType } from '@/components/LuckyDialog/LuckyTabs';
import { useActionsStore } from '@/store/actions';
import { Emoji } from '@/components/Emoji';
import {
  MsgAvatar,
  MsgBody,
  MsgExpireAnimate,
  MsgFooter,
  MsgFooterReplies,
  MsgHeader,
  MsgItem,
  MsgMenuTrigger,
  MsgReadStatus,
  MsgReply,
} from '../../msg-parts';

const previewCoverBgImgs = {
  [LuckyBoxGrabStatus.unClaim]: previewMsgLuckyBoxImg,
  [LuckyBoxGrabStatus.claimed]: previewMsgLuckyBoxOpenedImg,
  [LuckyBoxGrabStatus.claimedEnd]: previewMsgLuckyBoxGrayImg,
  [LuckyBoxGrabStatus.expired]: previewMsgLuckyBoxGrayImg,
};

export interface MsgLuckyBoxProps {
  session: Session;
  message: Msg;
}

export const MsgLuckyBox = memo(function MsgLuckyBox({ session, message }: MsgLuckyBoxProps) {
  const { t } = useTranslation();
  const { client, user: currentUser } = useStaticAppStore();
  const clickRef = useRef(false);
  const { user } = useUserInfo(message.owner_id);
  const setLuckyDetailsDialogIsOpen = useActionsStore(
    (store) => store.setLuckyDetailsDialogIsOpen
  );

  const img = useMemo(
    () => previewCoverBgImgs[message.payload.grabStatus as LuckyBoxGrabStatus],
    [message.payload.grabStatus]
  );

  const handleClickLuckyBox = useCallback(async () => {
    if (
      message.payload?.sendTo !== currentUser.user_id &&
      message.payload.boxType === LuckyType.Exclusive
    ) {
      toaster.warning({
        title: t('tips.notTheOwnerOfExclusiveBox'),
      });
      return;
    }
    if (message.payload?.boxStatus === 1) {
      toaster.warning({
        title: t('tips.luckyBoxFailedToSend'),
      });
      return;
    }

    if (!user) {
      return;
    }

    if (message.payload.grabStatus === LuckyBoxGrabStatus.unClaim) {
      const { accounts } = useWalletStore.getState();
      // TODO amo: Fix isDefault
      const defaultAccount = accounts.find((item) => item.isDefault && (item as any).uid);
      if (!defaultAccount) {
        toaster.error({
          title: t('tips.noDefaultAccount'),
        });
        return;
      }

      const walletAddress =
        defaultAccount.derivedWallets?.[0]?.seriesWallets[message.payload.chainType]
          ?.walletAddress;

      if (!walletAddress) {
        toaster.error({
          title: t('tips.noWalletAddress'),
        });
        return;
      }

      const data = await client?.Api.luckyBox.grabLuckyBoxCoinPacket({
        packet_no: message.payload.packetNo,
        wallet_address: walletAddress,
      });
      const { code, data: grabRes } = data ?? {};

      let grabStatus = LuckyBoxGrabStatus.unClaim;
      if (grabRes?.code === 0) {
        grabStatus = LuckyBoxGrabStatus.claimed;
      } else if (code === 10002) {
        grabStatus = LuckyBoxGrabStatus.claimedEnd;
      } else if (message.payload.deadline < dayjs().valueOf()) {
        grabStatus = LuckyBoxGrabStatus.expired;
      }

      const editRes = await client?.Api.messages.editLocalMessage(
        session!.chat_id,
        message.cli_msg_id,
        JSON.stringify({
          ...message.payload,
          grabStatus,
          grabAmount: grabRes?.amount,
        })
      );
      if (editRes) {
        const sessionStore = useAppStore.getState().sessionStores[session.chat_id]?.getState();
        sessionStore?.updateMessages([
          {
            cli_msg_id: message.cli_msg_id,
            payload: {
              grabStatus,
              grabAmount: grabRes?.amount,
            },
          },
        ]);
      }
      setLuckyDetailsDialogIsOpen(true, {
        user,
        currentUser,
        message: {
          ...message,
          payload: { ...message.payload, grabStatus, grabAmount: grabRes?.amount },
        },
      });
    } else {
      setLuckyDetailsDialogIsOpen(true, { user, currentUser, message });
    }
  }, [
    client?.Api.luckyBox,
    client?.Api.messages,
    currentUser,
    message,
    session,
    user,
    setLuckyDetailsDialogIsOpen,
    t,
  ]);

  const onClickLuckyBox = useCallback(
    async (message: Msg) => {
      const sessionStore = useAppStore.getState().sessionStores[session.chat_id]?.getState();

      if (
        sessionStore?.groupInfo?.status === GroupStatusEnum.DISBAND &&
        message.payload?.grabStatus === LuckyBoxGrabStatus.unClaim
      ) {
        toaster.warning({
          title: t('tips.notInGroup'),
        });
        return;
      }

      if (clickRef.current || message.payload?.boxStatus === LuckyBoxGrabStatus.claimed) {
        return;
      }

      clickRef.current = true;

      await handleClickLuckyBox?.().finally(() => {
        clickRef.current = false;
      });
    },
    [handleClickLuckyBox, session.chat_id, t]
  );

  return (
    <MsgItem message={message}>
      <MessageExpireContext message={message}>
        <MsgAvatar message={message} session={session} />
        <MsgBody message={message}>
          <MsgHeader message={message} session={session} />
          <MsgMenuTrigger message={message}>
            <MsgExpireAnimate session={session} message={message}>
              <MessageBubble bg="transparent">
                {message.link_msg_id && (
                  <MsgReply session={session} link_msg_id={message.link_msg_id} />
                )}
                <MessageLuckyBox
                  message={message}
                  previewCoverBgImg={img}
                  onClick={onClickLuckyBox}
                />
                <MessageMetaRoot>
                  <Emoji.MessageMeta chat_id={session.chat_id} message={message} />
                  <Center marginLeft="auto" gap="1.5">
                    <MsgReadStatus message={message} session={session} />
                  </Center>
                </MessageMetaRoot>
              </MessageBubble>
            </MsgExpireAnimate>
          </MsgMenuTrigger>
          <MsgFooter message={message}>
            <MsgFooterReplies message={message} />
          </MsgFooter>
        </MsgBody>
      </MessageExpireContext>
    </MsgItem>
  );
});
